import React, { useEffect, useState, useRef } from "react";
import { FormRestaurantScheme } from "../../../Models/Schemas/form-restaurants";
import { FORM_RESTAURANT_REGISTER } from "../../../Constants/Restaurant";
import Button from "../../Elements/Button/Button";
import { setErrorFormSimpleUtil } from "../../../Utilities/Forms/errors";
import useForm from "../../../Hooks/useForm";
import Input from "../../Elements/Input/Input";
import Loader from "../../Elements/Loader/Loader";
import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import MapPickerLeaflet from "./MapPickerLeaflet/MapPickerLeaflet";
/* import { forwardGeocode } from "../../../Utilities/geocode/geocode"; */
import { useNavigate } from "react-router-dom";
import { CATEGORIES } from "../../../Json/categorias";
import "./create-restaurant.css";

const CreateRestaurant = () => {
    const showToast = useToast();
    const lastGeoRef = useRef({ q: "", lat: null, lng: null });
    const { fetchDataRestaurantRegister, loading, error, success, fetchDataUploadProductImage } = useStore();

    // useForm
    const { values, errors, touched, handleBlur, handleChange, isValid } =
        useForm(FORM_RESTAURANT_REGISTER, FormRestaurantScheme);
    const navigate = useNavigate();
    const handledSuccessRef = useRef(false);
    const lastErrorRef = useRef(null);
    const [productos, setProductos] = useState([]);
    const [showAddProduct, setShowAddProduct] = useState(false);
    const [newProducto, setNewProducto] = useState({
        name: "",
        descripcion: "",
        precio: "",
        categoria: "",
        isAvailable: true,
        imageFile: null,
        imageUrl: "",
    });
    const [showMap, setShowMap] = useState(false);

    const setLatLngFromMap = (lat, lng, addr) => {
        handleChange("lat")({ target: { name: "lat", value: String(lat) } });
        handleChange("lng")({ target: { name: "lng", value: String(lng) } });
        if (addr) {
            handleChange("direccion")({ target: { name: "direccion", value: addr } });
            lastGeoRef.current.q = addr;
        }
        setShowMap(false);
    };

/*     const geocodeDireccionOnBlur = async () => {
        const q = (values.direccion || "").trim();
        if (!q || lastGeoRef.current.q === q) return; // evita repetir
        try {
            const res = await forwardGeocode(q, { email: "tu-correo@dominio.com" }); // opcional
            if (res[0]) {
                const { lat, lon, display_name } = res[0];
                handleChange("lat")({ target: { name: "lat", value: String(lat) } });
                handleChange("lng")({ target: { name: "lng", value: String(lon) } });
                handleChange("direccion")({ target: { name: "direccion", value: display_name || q } });
                lastGeoRef.current = { q: display_name || q, lat, lng: lon };
            } else {
                showToast("error", "No se encontró esa dirección");
            }
        } catch (e) {
            showToast("error", "Error geocodificando la dirección");
        }
    }; */

    // wrapper para mantener tu handleBlur original + geocoding
/*     const onDireccionBlur = (e) => {
        handleBlur("direccion")(e);
        geocodeDireccionOnBlur();
    }; */

    // Helper para soportar onChange(value) | onChange(name,value) | onChange(event)
    const getValueFromInput = (a, b) => {
        const candidate = b ?? a;
        if (candidate && typeof candidate === "object" && "target" in candidate) {
            return candidate.target?.value ?? "";
        }
        if (typeof candidate === "string" || typeof candidate === "number") {
            return String(candidate);
        }
        return "";
    };

    // Productos
    const onProdChange = (key) => (a, b) => {
        const value = getValueFromInput(a, b);
        setNewProducto((prev) => ({ ...prev, [key]: value }));
    };

    const onProdToggleAvailable = (e) => {
        const checked = e?.target?.checked ?? !!e;
        setNewProducto((prev) => ({ ...prev, isAvailable: checked }));
    };

    const addProducto = async () => {
        if (!newProducto.name || newProducto.precio === "") {
            showToast("error", "El producto necesita nombre y precio");
            return;
        }
        const precioNumber = Number(newProducto.precio);
        if (!Number.isFinite(precioNumber) || precioNumber < 0) {
            showToast("error", "El precio debe ser un número válido (>= 0)");
            return;
        }

        let imageUrl = "";
        if (newProducto.imageFile) {
            try {
                imageUrl = await fetchDataUploadProductImage({
                    restauranteId: values.restauranteId || "temp",
                    productName: newProducto.name,
                    file: newProducto.imageFile,
                });
            } catch (err) {
                showToast("error", "Error subiendo la imagen");
                return;
            }
        }

        setProductos((prev) => [
            ...prev,
            {
                name: newProducto.name.trim(),
                descripcion: newProducto.descripcion.trim(),
                precio: precioNumber,
                categoria: newProducto.categoria || "",
                isAvailable: !!newProducto.isAvailable,
                imageUrl,
            },
        ]);

        setNewProducto({ name: "", descripcion: "", precio: "", categoria: "", isAvailable: true, imageFile: null, imageUrl: "" });
        setShowAddProduct(false);
    };

    const removeProducto = (idx) => {
        setProductos((prev) => prev.filter((_, i) => i !== idx));
    };

    useEffect(() => {
        if (!success || success?.statusCode !== 201) return;
        if (handledSuccessRef.current) return;
        handledSuccessRef.current = true;

        showToast("success", success.message || "Comercio creado exitosamente 🎉");
        setProductos([]);
        setNewProducto({ name: "", descripcion: "", precio: "", categoria: "", isAvailable: true, imageFile: null, imageUrl: "" });

        Object.keys(values).forEach((key) => {
            handleChange(key)({ target: { name: key, value: "" } });
        });

        const t = setTimeout(() => {
            useStore.getState().resetSuccess();
            navigate("/", { replace: true });
        }, 1200);

        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success?.statusCode]);

    useEffect(() => {
        if (!error || error === lastErrorRef.current) return;
        lastErrorRef.current = error;

        const msg = typeof error === "string" ? error : (error.message || "Ocurrió un error");
        showToast("error", msg);
    }, [error, showToast]);

    const submit = async (e) => {
        e.preventDefault();
        lastErrorRef.current = null; // 👈 resetea antes de nueva request

        if (!Array.isArray(productos) || productos.length === 0) {
            showToast("error", "Debes agregar al menos un producto antes de registrar el comercio");
            return;
        }

        let imageUrl = null;

        // 👇 1) Subir imagen si existe
        if (values.imagen instanceof File) {
            const formData = new FormData();
            formData.append("file", values.imagen);

            try {
                const uploadResp = await fetchDataUploadProductImage(formData);
                if (uploadResp?.url) {
                    imageUrl = uploadResp.url;
                } else {
                    showToast("error", "Error al subir la imagen");
                    return;
                }
            } catch (err) {
                showToast("error", err.message || "Error subiendo la imagen");
                return;
            }
        }

        // 👇 2) Crear payload con la URL de la imagen
        const payload = {
            ...values,
            imagen: imageUrl, // reemplaza el file por la URL
            lat: values.lat === "" ? undefined : Number(values.lat),
            lng: values.lng === "" ? undefined : Number(values.lng),
            tiempoEntregaEstimado: values.tiempoEntregaEstimado === "" ? undefined : Number(values.tiempoEntregaEstimado),
            costoEnvio: 35, // fijo por ahora, no editable (ver EditMyRestaurant.jsx)
            isActive: false,
            productos,
            horarios: values.horarios || {},
        };

        // 👇 3) Registrar restaurante
        const res = await fetchDataRestaurantRegister(payload);

        if (res && (res.statusCode === 201 || res.code === 201 || res.ok === true)) {
            showToast("success", "Comercio creado exitosamente 🎉");

            // limpia estado local del form
            setProductos([]);
            setNewProducto({ name: "", descripcion: "", precio: "", categoria: "", isAvailable: true, imageFile: null, imageUrl: "" });
            Object.keys(values).forEach((key) => {
                handleChange(key)({ target: { name: key, value: "" } });
            });

            // opcional: resetea el success global para no re-disparar efectos
            useStore.getState().resetSuccess();

            // navega
            navigate("/", { replace: true });
            return;
        }

        if (Array.isArray(productos) && productos.length > 0) {
            try {
                const sanitized = productos.map(({ imageFile, ...rest }) => rest);
                localStorage.setItem("products_draft_last", JSON.stringify(sanitized));
            } catch (err) {
                console.error("Error guardando draft en localStorage", err);
            }
        }

    };

    if (loading) return <Loader />;

    return (
        <div className="container-fluid restaurant-wrapper flex justify-center align-center">
            <div className="form-restaurant flex justify-center">
                <div className="restaurant-header">
                    <h4>Registrar Comercio</h4>
                    <p className="restaurant-subtext">Agrega tu comercio al sistema 🍴</p>
                </div>

                <form onSubmit={submit}>
                    {/* Nombre */}
                    <Input
                        value={values.name}
                        label="Nombre del comercio"
                        onChange={handleChange("name")}
                        onBlur={handleBlur("name")}
                        error={setErrorFormSimpleUtil(errors, touched, "name")}
                        success={!setErrorFormSimpleUtil(errors, touched, "name")}
                    />

                    {/* Teléfono */}
                    <Input
                        value={values.phone}
                        label="Teléfono (10 dígitos)"
                        onChange={handleChange("phone")}
                        onBlur={handleBlur("phone")}
                        error={setErrorFormSimpleUtil(errors, touched, "phone")}
                        success={!setErrorFormSimpleUtil(errors, touched, "phone")}
                        maxLength={10}
                    />

                    {/* Correo */}
                    <Input
                        value={values.email}
                        label="Correo electrónico"
                        onChange={handleChange("email")}
                        onBlur={handleBlur("email")}
                        error={setErrorFormSimpleUtil(errors, touched, "email")}
                        success={!setErrorFormSimpleUtil(errors, touched, "email")}
                        type="email"
                    />

                    {/* Categorías */}
                    {/* Categoría principal */}
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Categoría principal
                        </label>
                        <select
                            value={values.categoriaPrincipal || ""}
                            onChange={(e) => {
                                const cat = e.target.value;
                                handleChange("categoriaPrincipal")({
                                    target: { name: "categoriaPrincipal", value: cat }
                                });
                                // reset de subcategorías si cambia la principal
                                handleChange("categorias")({
                                    target: { name: "categorias", value: [] }
                                });
                            }}
                            className="border rounded-md p-2"
                        >
                            <option value="">-- Selecciona una categoría --</option>
                            {CATEGORIES.map((cat) => (
                                <option key={cat.name} value={cat.name}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Subcategorías */}
                    {values.categoriaPrincipal && (
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Subcategorías de {values.categoriaPrincipal}
                            </label>
                            <div className="flex flex-col gap-2 border rounded-md p-3 cats-grid">
                                {CATEGORIES.find((c) => c.name === values.categoriaPrincipal)?.subcategories.map((sub) => (
                                    <label key={sub} className="flex items-center gap-2 cat-item">
                                        <input
                                            type="checkbox"
                                            checked={(values.categorias || []).includes(sub)}
                                            onChange={(e) => {
                                                let newCats = [...(values.categorias || [])];
                                                if (e.target.checked) newCats.push(sub);
                                                else newCats = newCats.filter((c) => c !== sub);
                                                handleChange("categorias")({
                                                    target: { name: "categorias", value: newCats }
                                                });
                                            }}
                                        />
                                        <span>{sub}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div style={{ display: "grid", gap: 10 }}>
                        {/* <Input
                            value={values.direccion}
                            label="Dirección"
                            onChange={handleChange("direccion")}
                            onBlur={onDireccionBlur}
                            error={setErrorFormSimpleUtil(errors, touched, "direccion")}
                            success={!setErrorFormSimpleUtil(errors, touched, "direccion")}
                            placeholder="Calle y número, colonia, ciudad"
                        /> */}

                        <Input
                            value={values.direccion}
                            label="Dirección"
                            onClick={() => setShowMap(true)}   // 👈 abre mapa al click
                            readOnly                           // 👈 evita escribir
                            error={setErrorFormSimpleUtil(errors, touched, "direccion")}
                            success={!setErrorFormSimpleUtil(errors, touched, "direccion")}
                            placeholder="Selecciona tu dirección en el mapa"
                        />

                        {/*                         <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <Input
                                value={values.lat}
                                label="Latitud"
                                onChange={handleChange("lat")}
                                onBlur={handleBlur("lat")}
                                error={setErrorFormSimpleUtil(errors, touched, "lat")}
                                success={!setErrorFormSimpleUtil(errors, touched, "lat")}
                                type="number"
                                step="0.000001"
                                placeholder="19.432608"
                                readOnly
                            />
                            <Input
                                value={values.lng}
                                label="Longitud"
                                onChange={handleChange("lng")}
                                onBlur={handleBlur("lng")}
                                error={setErrorFormSimpleUtil(errors, touched, "lng")}
                                success={!setErrorFormSimpleUtil(errors, touched, "lng")}
                                type="number"
                                step="0.000001"
                                placeholder="-99.133209"
                                readOnly
                            />
                        </div> */}

                        {/* Un solo botón para abrir el mapa (search + ajuste fino + reverse geocode) */}
                       {/*  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <Button
                                text="Elegir ubicación en mapa"
                                color="blue"
                                fill
                                type="button"
                                action={() => setShowMap(true)}
                            />
                            {values.lat && values.lng && (
                                <small className="opacity-70">
                                    Seleccionado: {Number(values.lat).toFixed(6)}, {Number(values.lng).toFixed(6)}
                                </small>
                            )}
                        </div> */}
                    </div>

                    {showMap && (
                        <MapPickerLeaflet
                            initialLat={values.lat ? Number(values.lat) : 19.432608}
                            initialLng={values.lng ? Number(values.lng) : -99.133209}
                            geocodeEmail="tu-correo@dominio.com"
                            onPick={setLatLngFromMap}
                            onClose={() => setShowMap(false)}
                        />
                    )}

                    {/* Horarios */}
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Horarios de atención
                        </label>

                        <div className="flex flex-col gap-2 border rounded-md p-3">
                            {[
                                "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"
                            ].map((day) => {
                                const abierto = values.horarios?.[day]?.open || false;
                                const apertura = values.horarios?.[day]?.apertura || "";
                                const cierre = values.horarios?.[day]?.cierre || "";

                                return (
                                    <div key={day} className="grid grid-cols-[auto,1fr,1fr,1fr] items-center gap-3">
                                        {/* Checkbox */}
                                        <input
                                            type="checkbox"
                                            checked={abierto}
                                            onChange={(e) => {
                                                const newHorarios = { ...(values.horarios || {}) };
                                                newHorarios[day] = e.target.checked
                                                    ? { open: true, apertura: "09:00", cierre: "18:00" }
                                                    : { open: false, apertura: "", cierre: "" };
                                                handleChange("horarios")({ target: { name: "horarios", value: newHorarios } });
                                            }}
                                        />

                                        {/* Día */}
                                        <span className="font-medium text-gray-700">{day}</span>

                                        {/* Hora apertura */}
                                        <input
                                            type="time"
                                            className="border rounded-md px-2 py-1 disabled:bg-gray-100"
                                            value={apertura}
                                            disabled={!abierto}
                                            onChange={(e) => {
                                                const newHorarios = { ...(values.horarios || {}) };
                                                newHorarios[day] = { ...(newHorarios[day] || {}), apertura: e.target.value };
                                                handleChange("horarios")({ target: { name: "horarios", value: newHorarios } });
                                            }}
                                        />

                                        {/* Hora cierre */}
                                        <input
                                            type="time"
                                            className="border rounded-md px-2 py-1 disabled:bg-gray-100"
                                            value={cierre}
                                            disabled={!abierto}
                                            onChange={(e) => {
                                                const newHorarios = { ...(values.horarios || {}) };
                                                newHorarios[day] = { ...(newHorarios[day] || {}), cierre: e.target.value };
                                                handleChange("horarios")({ target: { name: "horarios", value: newHorarios } });
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tiempo estimado */}
                    <Input
                        value={values.tiempoEntregaEstimado}
                        label="Tiempo estimado de entrega (min)"
                        onChange={handleChange("tiempoEntregaEstimado")}
                        onBlur={handleBlur("tiempoEntregaEstimado")}
                        error={setErrorFormSimpleUtil(errors, touched, "tiempoEntregaEstimado")}
                        success={!setErrorFormSimpleUtil(errors, touched, "tiempoEntregaEstimado")}
                        type="number"
                        min={10}
                        max={120}
                    />

                    {/* Costo de envío */}
                    <Input
                        value={35}
                        label="Costo de envío"
                        type="number"
                        disabled
                    />

                    {/* Activo */}
                    <div className="mt-2">
                        <label className="flex items-center gap-2 opacity-60">
                            <input type="checkbox" checked={false} readOnly disabled />
                            <span>Activo (se activará después por un administrador)</span>
                        </label>
                    </div>

                    {/* ---- Productos (solo front) ---- */}
                    <center>
                        <Button
                            text="Agregar producto"
                            color="blue"
                            fill
                            action={() => setShowAddProduct(true)}
                            classNames="mt-3"
                            type="button"
                        />
                    </center>

                    {showAddProduct && (
                        <div className="mt-3 p-3 rounded-lg border">
                            <Input
                                label="Nombre del producto"
                                value={newProducto.name}
                                onChange={onProdChange("name")}
                                onBlur={() => { }}
                                error={false}
                                success={!!newProducto.name}
                            />

                            <Input
                                label="Descripción"
                                value={newProducto.descripcion}
                                onChange={onProdChange("descripcion")}
                                onBlur={() => { }}
                                error={false}
                                success={!!newProducto.descripcion}
                            />

                            <Input
                                label="Precio"
                                type="number"
                                value={newProducto.precio}
                                onChange={onProdChange("precio")}
                                onBlur={() => { }}
                                error={false}
                                success={newProducto.precio !== ""}
                                step="0.01"
                                min="0"
                            />

                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Categoría del producto
                                </label>
                                <select
                                    value={newProducto.categoria}
                                    onChange={(e) => setNewProducto(prev => ({ ...prev, categoria: e.target.value }))}
                                    className="border rounded-md p-2 w-full"
                                >
                                    <option value="">-- Sin categoría --</option>
                                    {["Comida", "Bebida", "Postre", "Antojo", "Otro"].map(cat => (
                                        <option key={cat} value={cat.toLowerCase()}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="productImage" className="block text-sm font-medium text-gray-700">
                                    Imagen del producto
                                </label>
                                <input
                                    id="productImage"
                                    type="file"
                                    accept="image/*"
                                    className="mt-1 block w-full text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm cursor-pointer focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;

                                        try {
                                            const imageUrl = await fetchDataUploadProductImage({
                                                restauranteId: values.restauranteId || "temp",
                                                productName: newProducto.name || file.name,
                                                file,
                                            });

                                            setNewProducto((prev) => ({ ...prev, imageFile: file, imageUrl }));
                                            showToast("success", "Imagen subida correctamente ✅");
                                        } catch (err) {
                                            showToast("error", "Error subiendo imagen");
                                        }
                                    }}
                                />
                                {newProducto.imageUrl && (
                                    <div
                                        className="mt-2 w-full h-40 rounded border border-gray-300"
                                        style={{
                                            backgroundImage: `url(${newProducto.imageUrl})`,
                                            backgroundSize: "cover",
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition: "center",
                                            width: "100%",
                                            height: "400px",
                                        }}
                                    />
                                )}

                            </div>

                            <div className="mt-2">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={!!newProducto.isAvailable}
                                        onChange={onProdToggleAvailable}
                                    />
                                    <span>Disponible</span>
                                </label>
                            </div>

                            {/* ✅ Validación aquí */}
                            <div className="mt-3 flex gap-2 justify-center">
                                <Button
                                    text="Guardar producto"
                                    color="blue"
                                    fill
                                    action={addProducto}
                                    type="button"
                                    disabled={
                                        !newProducto.name.trim() ||
                                        !newProducto.descripcion.trim() ||
                                        newProducto.precio === "" ||
                                        !newProducto.imageUrl
                                    }
                                />
                                <Button
                                    text="Cancelar"
                                    color="gray"
                                    fill
                                    action={() => {
                                        setShowAddProduct(false);
                                        setNewProducto({ name: "", descripcion: "", precio: "", categoria: "", isAvailable: true, imageFile: null, imageUrl: "" });
                                    }}
                                    type="button"
                                />
                            </div>
                        </div>
                    )}

                    {productos.length > 0 && (
                        <div className="mt-3">
                            <h5>Productos agregados (no se envían aún):</h5>
                            <ul className="mt-1">
                                {productos.map((p, idx) => (
                                    <li key={idx} className="flex items-center justify-between py-1">
                                        <span>
                                            <strong>{p.name}</strong> — ${p.precio.toFixed(2)} {p.isAvailable ? "✅" : "⛔"}
                                            {p.categoria && <span style={{ marginLeft: 6, fontSize: 12, color: "#666" }}>({p.categoria})</span>}
                                            {p.imageUrl && <img src={p.imageUrl} alt={p.name} style={{ width: 50, marginLeft: 10 }} />}
                                        </span>
                                        <Button text="Quitar" color="red" action={() => removeProducto(idx)} type="button" />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <center>
                        <Button
                            text="Registrar comercio"
                            color="green"
                            fill
                            disabled={!isValid || loading || productos.length === 0}
                            classNames="mt-3"
                            type="submit"
                        />

                    </center>
                </form>
            </div>
        </div>
    );
};

export default CreateRestaurant;
